import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import {
  ArrowDownIcon,
  ArrowUpIcon,
  SortIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  EyeSlashIcon,
} from '../../../icons'
import { ChevronDownIcon } from '../../../icons/chevron-down-icon'
import { Button } from '../../core/button'
import { Skeleton } from '../../core/skeleton'

import {
  generateClassesExpandableIcon,
  generateClassesSortColumn,
  generateClassesOfTableControl,
} from './table-interactive.constant'
import { Text } from '../../core'
import { Tooltip } from '../../core/tooltip'

import { useCookieState } from '@/helpers'

import {
  TableInteractiveColumnProps,
  TableInteractiveProps,
} from './table-interactive.type'

import styles from './table-interactive.module.css'

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'

import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable'

import { CSS } from '@dnd-kit/utilities'

export const TableInteractive = <T,>({
  columns,
  actionsColumn,
  data,
  isLoading = false,
  classNames = {},
  expandableComponent,
  selectedRow,
  onSelectRow,
  sortBy = '',
  onSortBy,
  allowColumnResize = true,
  allowColumnReorder = true,
  cookieKey,
  onColumnsChange,
  columnOrder: externalColumnOrder,
  translations = {},
}: TableInteractiveProps<T>) => {
  const sortByText = translations.sortBy ?? 'Ordenar por'
  const sortAscText = translations.sortAsc ?? 'Orden ascendente'
  const sortDescText = translations.sortDesc ?? 'Orden descendente'
  const moveToStartText = translations.moveToStart ?? 'Mover al inicio'
  const moveToEndText = translations.moveToEnd ?? 'Mover al final'

  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())
  const [columnWidths, setColumnWidths] = useState<Record<string, string>>({})
  const headerRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const headerTitleRefs = useRef<Record<string, HTMLSpanElement | null>>({})
  const resizingRef = useRef<{
    key: string
    startX: number
    startWidth: number
  } | null>(null)
  const [isResizing, setIsResizing] = useState(false)
  const actionsKey = actionsColumn?.key

  const [internalColumnOrder, setInternalColumnOrder] = useState<string[]>(
    columns.filter((c) => c.key !== actionsKey).map((c) => c.key),
  )
  const columnOrderRef = useRef<string[]>(internalColumnOrder)

  const lastColumnWidthsRef = useRef<Record<string, string>>({})

  let persistedColumns:
    | { order?: string[]; widths?: Record<string, string> }
    | undefined
  let setPersistedColumns:
    | ((v: { order?: string[]; widths?: Record<string, string> }) => void)
    | undefined

  if (cookieKey) {
    const tuple = useCookieState<{
      order?: string[]
      widths?: Record<string, string>
    }>(cookieKey, {})

    persistedColumns = tuple[0]
    setPersistedColumns = tuple[1]
  }

  const [activeId, setActiveId] = useState<string | null>(null)
  const sensors = useSensors(useSensor(PointerSensor))
  const [headerOverflow, setHeaderOverflow] = useState<Record<string, boolean>>(
    {},
  )

  const displayData = useMemo(
    () => (isLoading ? Array.from({ length: 10 }, (_, i) => i as T) : data),
    [data, isLoading],
  )

  const toggleRowExpansion = useCallback((index: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(index)) {
        newSet.delete(index)
      } else {
        newSet.add(index)
      }
      return newSet
    })
  }, [])

  useEffect(() => {
    if (isResizing) {
      document.body.classList.add(styles['resizing-global'])
    } else {
      document.body.classList.remove(styles['resizing-global'])
    }

    return () => {
      document.body.classList.remove(styles['resizing-global'])
    }
  }, [isResizing])

  useEffect(() => {
    if (
      persistedColumns &&
      persistedColumns.order &&
      persistedColumns.order.length > 0
    ) {
      const filtered = persistedColumns.order.filter((k) =>
        columns.some((c) => c.key === k),
      )
      const others = columns
        .map((c) => c.key)
        .filter((k) => !filtered.includes(k))
      const next = [...filtered, ...others]
      if (externalColumnOrder && externalColumnOrder.length) {
        columnOrderRef.current = externalColumnOrder
      } else {
        setInternalColumnOrder(next)
        columnOrderRef.current = next
      }

      if (persistedColumns.widths) {
        setColumnWidths(persistedColumns.widths)
        lastColumnWidthsRef.current = persistedColumns.widths
      }
      return
    }

    const next = columns.filter((c) => c.key !== actionsKey).map((c) => c.key)
    if (externalColumnOrder && externalColumnOrder.length) {
      columnOrderRef.current = externalColumnOrder
    } else {
      setInternalColumnOrder(next)
      columnOrderRef.current = next
    }
  }, [columns, persistedColumns])

  useEffect(() => {
    columnOrderRef.current =
      externalColumnOrder && externalColumnOrder.length
        ? externalColumnOrder
        : internalColumnOrder
  }, [internalColumnOrder, externalColumnOrder])

  useEffect(() => {
    lastColumnWidthsRef.current = columnWidths
  }, [columnWidths])

  const savePersistentState = useCallback(() => {
    const order = columnOrderRef.current
    const widths = lastColumnWidthsRef.current

    if (!externalColumnOrder && cookieKey && setPersistedColumns) {
      try {
        setPersistedColumns({ order, widths })
      } catch (err) { }
    }

    onColumnsChange?.({ order, widths })
  }, [cookieKey, onColumnsChange, externalColumnOrder])

  const isControlled = externalColumnOrder !== undefined
  const activeOrder = isControlled ? externalColumnOrder! : internalColumnOrder

  const orderedColumns = useMemo(() => {
    const normalColumns = columns.filter((c) => c.key !== actionsKey)
    return activeOrder
      .map((key) => normalColumns.find((c) => c.key === key))
      .filter(Boolean) as TableInteractiveColumnProps<T>[]
  }, [activeOrder, columns, actionsKey])

  const getValueByPath = useCallback(<U,>(object: U, path: string): string => {
    try {
      const keys = path.split('.')
      let result: unknown = object

      for (const key of keys) {
        if (result == null || typeof result !== 'object') {
          return '-'
        }
        result = (result as Record<string, unknown>)[key]
      }

      return result?.toString() ?? '-'
    } catch {
      return '-'
    }
  }, [])

  const SortButton = useCallback(
    ({ sortKey, columnKey }: { sortKey?: string; columnKey?: string }) => {
      const triggerRef = useRef<HTMLButtonElement | null>(null)
      const portalRef = useRef<HTMLDivElement | null>(null)
      const [open, setOpen] = useState(false)
      const [pos, setPos] = useState<{ top: number; left: number }>({
        top: 0,
        left: 0,
      })

      const cleanSortKey = sortBy?.replace('-', '')
      const isDesc = sortBy?.startsWith('-')
      const isActive = cleanSortKey === sortKey
      const isAscSelected = isActive && !isDesc
      const isDescSelected = isActive && isDesc

      useEffect(() => {
        if (!open) return

        const onDocClick = (e: MouseEvent) => {
          if (!triggerRef.current) return
          if (triggerRef.current.contains(e.target as Node)) return
          if (portalRef.current)
            if (portalRef.current.contains(e.target as Node)) return
          setOpen(false)
        }

        const onEsc = (e: KeyboardEvent) => {
          if (e.key === 'Escape') setOpen(false)
        }

        document.addEventListener('mousedown', onDocClick)
        document.addEventListener('keydown', onEsc)
        return () => {
          document.removeEventListener('mousedown', onDocClick)
          document.removeEventListener('keydown', onEsc)
        }
      }, [open])

      const openMenu = () => {
        const el = triggerRef.current
        if (!el) return
        const rect = el.getBoundingClientRect()
        setPos({ top: rect.bottom + 8, left: rect.left })
        setOpen(true)
      }

      const handleChoose = (mode: 'asc' | 'desc') => {
        if (!sortKey) return
        const target = mode === 'asc' ? sortKey : `-${sortKey}`
        if (sortBy === target) {
          onSortBy?.('')
        } else {
          onSortBy?.(target)
        }
        setOpen(false)
      }

      const moveToStart = () => {
        if (!columnKey) return
        const prev = activeOrder
        const next = prev.filter((k) => k !== columnKey)
        next.unshift(columnKey)
        columnOrderRef.current = next
        if (isControlled) {
          onColumnsChange?.({
            order: next,
            widths: lastColumnWidthsRef.current,
          })
        } else {
          setInternalColumnOrder(next)
          savePersistentState()
        }
        setOpen(false)
      }

      const moveToEnd = () => {
        if (!columnKey) return
        const prev = activeOrder
        const next = prev.filter((k) => k !== columnKey)
        next.push(columnKey)
        columnOrderRef.current = next
        if (isControlled) {
          onColumnsChange?.({
            order: next,
            widths: lastColumnWidthsRef.current,
          })
        } else {
          setInternalColumnOrder(next)
          savePersistentState()
        }
        setOpen(false)
      }

      return (
        <>
          <button
            ref={triggerRef}
            className={`${styles['sort-button']} ${isActive ? styles.active : ''}`}
            onClick={() => (open ? setOpen(false) : openMenu())}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            type="button"
          >
            {isActive ? (
              isDesc ? (
                <ArrowDownIcon />
              ) : (
                <ArrowUpIcon />
              )
            ) : (
              <SortIcon />
            )}
          </button>

          {open &&
            createPortal(
              <div
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                ref={portalRef}
                className={styles['sort-menu-portal']}
                style={{
                  position: 'fixed',
                  top: pos.top,
                  left: pos.left,
                  zIndex: 9999,
                }}
              >
                {sortKey && (
                  <div className={styles['sort-menu']}>
                    <Text
                      variant="text-small"
                      className={styles['sort-menu-title']}
                    >
                      {sortByText}:
                    </Text>
                    <button
                      className={generateClassesSortColumn(isAscSelected)}
                      onClick={() => handleChoose('asc')}
                      aria-pressed={isAscSelected}
                    >
                      {sortAscText}
                    </button>
                    <button
                      className={generateClassesSortColumn(isDescSelected)}
                      onClick={() => handleChoose('desc')}
                      aria-pressed={isDescSelected}
                    >
                      {sortDescText}
                    </button>
                  </div>
                )}
                <div className={styles['move-menu']}>
                  <button
                    className={styles['move-menu-item']}
                    onClick={moveToStart}
                  >
                    {moveToStartText}
                  </button>
                  <button
                    className={styles['move-menu-item']}
                    onClick={moveToEnd}
                  >
                    {moveToEndText}
                  </button>
                </div>
              </div>,
              document.body,
            )}
        </>
      )
    },
    [sortBy, onSortBy],
  )

  useEffect(() => {
    setExpandedRows(new Set())
  }, [data])

  const setHeaderRef = useCallback(
    (key: string) => (el: HTMLDivElement | null) => {
      headerRefs.current[key] = el
    },
    [],
  )

  const setHeaderTitleRef = useCallback(
    (key: string) => (el: HTMLSpanElement | null) => {
      headerTitleRefs.current[key] = el
    },
    [],
  )

  const checkHeaderOverflow = useCallback((key: string) => {
    const el = headerTitleRefs.current[key]
    if (!el) return
    const isOverflowing = el.scrollWidth > el.clientWidth
    setHeaderOverflow((prev) => ({ ...prev, [key]: isOverflowing }))
  }, [])

  useEffect(() => {
    const onResize = () => {
      Object.keys(headerTitleRefs.current).forEach((k) =>
        checkHeaderOverflow(k),
      )
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [checkHeaderOverflow])

  const startResize = useCallback(
    (e: React.MouseEvent, key: string) => {
      if (!allowColumnResize) return
      e.preventDefault()
      const el = headerRefs.current[key]

      if (!el) return

      const startX = e.clientX
      const startWidth = el.getBoundingClientRect().width

      resizingRef.current = { key, startX, startWidth }

      setIsResizing(true)

      const handleMove = (moveEvent: MouseEvent) => {
        if (!resizingRef.current) return

        const delta = moveEvent.clientX - resizingRef.current.startX
        const newWidth = Math.max(40, resizingRef.current.startWidth + delta)

        setColumnWidths((prev) => ({
          ...prev,
          [resizingRef.current!.key]: `${newWidth}px`,
        }))
      }

      const handleUp = () => {
        // persist and notify
        savePersistentState()
        resizingRef.current = null

        setIsResizing(false)

        document.removeEventListener('mousemove', handleMove)
        document.removeEventListener('mouseup', handleUp)
      }

      document.addEventListener('mousemove', handleMove)
      document.addEventListener('mouseup', handleUp)
    },
    [allowColumnResize, savePersistentState],
  )

  // Sortable header cell component using dnd-kit
  function SortableHeaderCell({
    column,
  }: {
    column: TableInteractiveColumnProps<T>
  }) {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: column.key })

    const appliedWidth = columnWidths[column.key] ?? column.width
    const baseStyle = appliedWidth
      ? ({
        minWidth: appliedWidth,
        maxWidth: appliedWidth,
        flex: `0 0 ${appliedWidth}`,
      } as React.CSSProperties)
      : ({
        minWidth: '120px',
      } as React.CSSProperties)

    const style: React.CSSProperties = {
      ...baseStyle,
      transform: CSS.Transform.toString(transform),
      transition,
      zIndex: isDragging ? 9999 : undefined,
      opacity: isDragging ? 0.6 : 1,
    }

    const setRefs = (el: HTMLDivElement | null) => {
      setNodeRef(el)
      setHeaderRef(column.key)(el)
    }

    const headerClass = [
      styles['header-cell'],
      allowColumnReorder ? styles.draggable : '',
      isDragging ? styles.dragging : '',
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <div key={column.key} className={headerClass} style={style} ref={setRefs}>
        <div
          className={styles['header-content']}
          {...(allowColumnReorder ? { ...attributes } : {})}
          {...(allowColumnReorder ? { ...listeners } : {})}
        >
          {column.renderHeader ? (
            column.renderHeader({ title: column.title || '' })
          ) : (
            <span
              className={styles['header-title']}
              ref={setHeaderTitleRef(column.key)}
              onMouseEnter={() => checkHeaderOverflow(column.key)}
            >
              {headerOverflow[column.key] ? (
                <Tooltip text={column.title || ''} orientation="top">
                  <span>{column.title}</span>
                </Tooltip>
              ) : (
                column.title
              )}
            </span>
          )}
          <SortButton sortKey={column.sortKey} columnKey={column.key} />
        </div>
        {allowColumnResize && (
          <div
            className={styles['resize-handle']}
            onMouseDown={(e) => startResize(e, column.key)}
            role="separator"
            aria-orientation="vertical"
          />
        )}
      </div>
    )
  }

  const renderCell = useCallback(
    (column: TableInteractiveColumnProps<T>, record: T, index: number) => {
      const value = getValueByPath(record, String(column.dataKey || ''))
      const appliedWidth = columnWidths[column.key] ?? column.width
      const style = appliedWidth
        ? ({
          minWidth: appliedWidth,
          maxWidth: appliedWidth,
          flex: `0 0 ${appliedWidth}`,
        } as React.CSSProperties)
        : ({
          minWidth: '120px',
        } as React.CSSProperties)

      const baseClassName = `${styles.cell} ${selectedRow === index ? styles['selected-cell'] : ''
        } ${typeof classNames.bodyColumn === 'function'
          ? classNames.bodyColumn({ record, index })
          : classNames.bodyColumn || ''
        }`.trim()

      if (isLoading) {
        return (
          <div
            key={`loading-${column.key}-${index}`}
            className={`${baseClassName} ${styles['loading-cell']}`}
            style={style}
          >
            {column.renderLoading || <Skeleton height="1rem" width="80%" />}
          </div>
        )
      }

      return (
        <div
          key={column.key}
          className={baseClassName}
          style={style}
          onClick={() => onSelectRow?.(index)}
          role={onSelectRow ? 'button' : undefined}
          tabIndex={onSelectRow ? 0 : undefined}
          onKeyDown={(e) => {
            if (onSelectRow && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault()
              onSelectRow(index)
            }
          }}
        >
          {column.render
            ? column.render({
              value: value === '-' ? undefined : value,
              record,
              index,
            })
            : value}
        </div>
      )
    },
    [
      getValueByPath,
      isLoading,
      selectedRow,
      onSelectRow,
      classNames.bodyColumn,
      columnWidths,
    ],
  )

  const renderHeaderCell = useCallback(
    (column: TableInteractiveColumnProps<T>) => {
      const appliedWidth = columnWidths[column.key] ?? column.width
      const style = appliedWidth
        ? ({
          minWidth: appliedWidth,
          maxWidth: appliedWidth,
          flex: `0 0 ${appliedWidth}`,
        } as React.CSSProperties)
        : ({
          minWidth: '120px',
        } as React.CSSProperties)
      const headerClass = [
        styles['header-cell'],
        allowColumnReorder ? styles.draggable : '',
      ]
        .filter(Boolean)
        .join(' ')

      return (
        <div
          key={column.key}
          className={headerClass}
          style={style}
          ref={setHeaderRef(column.key)}
        >
          <div className={styles['header-content']}>
            {column.renderHeader ? (
              column.renderHeader({ title: column.title || '' })
            ) : (
              <span
                className={styles['header-title']}
                ref={setHeaderTitleRef(column.key)}
                onMouseEnter={() => checkHeaderOverflow(column.key)}
              >
                {headerOverflow[column.key] ? (
                  <Tooltip text={column.title || ''} orientation="top">
                    <span>{column.title}</span>
                  </Tooltip>
                ) : (
                  column.title
                )}
              </span>
            )}
            <SortButton sortKey={column.sortKey} columnKey={column.key} />
          </div>
          {allowColumnResize && (
            <div
              className={styles['resize-handle']}
              onMouseDown={(e) => startResize(e, column.key)}
              role="separator"
              aria-orientation="vertical"
            />
          )}
        </div>
      )
    },
    [
      SortButton,
      columnWidths,
      setHeaderRef,
      startResize,
      allowColumnReorder,
      activeOrder,
      savePersistentState,
    ],
  )
  /* Scroll Shadow Logic */
  const containerRef = useRef<HTMLDivElement>(null)
  const [scrollState, setScrollState] = useState({
    left: false,
    right: false,
  })

  const checkScroll = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    const { scrollLeft, scrollWidth, clientWidth } = el
    setScrollState({
      left: scrollLeft > 0,
      right: Math.ceil(scrollLeft + clientWidth) < scrollWidth,
    })
  }, [])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    checkScroll()
    el.addEventListener('scroll', checkScroll)
    window.addEventListener('resize', checkScroll)
    return () => {
      el.removeEventListener('scroll', checkScroll)
      window.removeEventListener('resize', checkScroll)
    }
  }, [checkScroll, data, orderedColumns])

  /* Floating Controls Logic */
  const [isShowBorder, setIsShowBorder] = useState(false)

  const scrollLeft = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: -300, behavior: 'smooth' })
    }
  }, [])

  const scrollRight = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: 300, behavior: 'smooth' })
    }
  }, [])

  return (
    <div className={styles['interactive-table-wrap']} style={{ position: 'relative' }}>
      <div
        className={`${styles.container} ${classNames.wrap || ''} ${isShowBorder ? styles['with-border'] : ''}`}
        ref={containerRef}
        data-scroll-left={scrollState.left}
        data-scroll-right={scrollState.right}
      >
        <div className={styles['table-wrapper']}>
          <div className={styles['main-table']}>
            <div className={styles.header}>
              {expandableComponent && (
                <div
                  className={`${styles['header-cell']} ${styles['expansion-header']}`}
                ></div>
              )}
              {allowColumnReorder ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={(e: DragStartEvent) =>
                    setActiveId(e.active.id as string)
                  }
                  onDragEnd={(e: DragEndEvent) => {
                    setActiveId(null)
                    const { active, over } = e
                    if (!over) return
                    const oldIndex = activeOrder.indexOf(active.id as string)
                    const newIndex = activeOrder.indexOf(over.id as string)
                    if (
                      oldIndex !== -1 &&
                      newIndex !== -1 &&
                      oldIndex !== newIndex
                    ) {
                      const next = arrayMove(activeOrder, oldIndex, newIndex)
                      columnOrderRef.current = next
                      if (isControlled) {
                        onColumnsChange?.({
                          order: next,
                          widths: lastColumnWidthsRef.current,
                        })
                      } else {
                        setInternalColumnOrder(next)
                        savePersistentState()
                      }
                    }
                  }}
                >
                  <SortableContext
                    items={activeOrder}
                    strategy={horizontalListSortingStrategy}
                  >
                    {orderedColumns.map((column) => (
                      <SortableHeaderCell key={column.key} column={column} />
                    ))}
                  </SortableContext>

                  <DragOverlay>
                    {activeId ? (
                      <div
                        className={`${styles['header-cell']} ${styles.dragging}`}
                      >
                        <div className={styles['header-content']}>
                          <span className={styles['header-title']}>
                            {columns.find((c) => c.key === activeId)?.title ||
                              activeId}
                          </span>
                        </div>
                      </div>
                    ) : null}
                  </DragOverlay>
                </DndContext>
              ) : (
                orderedColumns.map((column) => renderHeaderCell(column))
              )}
              {actionsColumn && (
                <div
                  key={actionsColumn.key}
                  className={`${styles['header-cell']} ${styles['actions-header']}`}
                  style={
                    actionsColumn.width
                      ? ({
                        minWidth: actionsColumn.width,
                        maxWidth: actionsColumn.width,
                        flex: `0 0 ${actionsColumn.width}`,
                      } as React.CSSProperties)
                      : ({
                        minWidth: '2.75rem',
                        maxWidth: '2.75rem',
                        flex: '0 0 2.75rem',
                      } as React.CSSProperties)
                  }
                >
                  <div className={styles['header-content']}>
                    {actionsColumn.renderHeader ? (
                      actionsColumn.renderHeader({
                        title: actionsColumn.title || '',
                      })
                    ) : (
                      <span className={styles['header-title']}>
                        {actionsColumn.title}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className={styles.body}>
              {displayData.map((record, index) => (
                <div key={index} className={styles['row-group']}>
                  <div className={styles.row}>
                    {expandableComponent && (
                      <div
                        className={`${styles.cell} ${styles['expansion-cell']} ${selectedRow === index ? styles['selected-cell'] : ''
                          } ${typeof classNames.bodyColumn === 'function'
                            ? classNames.bodyColumn({ record, index })
                            : classNames.bodyColumn || ''
                          }`.trim()}
                      >
                        <Button
                          variant="ghost"
                          model="round"
                          onClick={() => toggleRowExpansion(index)}
                          className={generateClassesExpandableIcon(
                            expandedRows.has(index),
                          )}
                        >
                          <ChevronDownIcon />
                        </Button>
                      </div>
                    )}

                    {orderedColumns.map((column) =>
                      renderCell(column, record, index),
                    )}

                    {actionsColumn && (
                      <div
                        key={actionsColumn.key}
                        className={`${styles.cell} ${styles['actions-cell']}`}
                        style={
                          actionsColumn.width
                            ? ({
                              minWidth: actionsColumn.width,
                              maxWidth: actionsColumn.width,
                              flex: `0 0 ${actionsColumn.width}`,
                            } as React.CSSProperties)
                            : ({
                              minWidth: '2.75rem',
                              maxWidth: '2.75rem',
                              flex: '0 0 2.75rem',
                            } as React.CSSProperties)
                        }
                      >
                        {actionsColumn.render
                          ? actionsColumn.render({
                            value: undefined,
                            record,
                            index,
                          })
                          : null}
                      </div>
                    )}
                  </div>

                  {expandableComponent && (
                    <div
                      className={`${styles['expanded-row']} ${expandedRows.has(index) ? styles.open : ''
                        } ${classNames.expandedContent || ''}`}
                    >
                      <div className={styles['expanded-content']}>
                        {expandableComponent({ record, index })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div
        className={generateClassesOfTableControl(isShowBorder, false)}
        style={{ position: 'absolute', top: '0.20rem', right: '0.20rem', zIndex: 100 }}
      >
        <div className={styles['table-control-buttons-wrap']}>
          <Button
            size="small"
            variant="secondary"
            onClick={() => setIsShowBorder(!isShowBorder)}
            model="square"
            className={`${styles['table-control-button']} ${styles['hidden-buttons']}`}
          >
            {isShowBorder ? <EyeSlashIcon /> : <EyeIcon />}
          </Button>
          <Button
            size="small"
            variant="secondary"
            onClick={scrollLeft}
            model="square"
            className={styles['table-control-button']}
          >
            <ChevronLeftIcon />
          </Button>
          <Button
            size="small"
            variant="secondary"
            onClick={scrollRight}
            model="square"
            className={styles['table-control-button']}
          >
            <ChevronRightIcon />
          </Button>
        </div>
      </div>
    </div>

  )
}

export default TableInteractive

